import React, { useState, useEffect } from 'react';
import { 
  Box, Grid, Card, CardContent, Typography, Paper, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Divider, useTheme
} from '@mui/material';
import { getCountryName } from '../../../utils/countryUtils';

// Компонент для отображения статистики по странам и городам
const CountryCityStatistics = ({ countryData, cityData }) => {
  const theme = useTheme();
  
  // Проверяем наличие данных
  if ((!countryData || countryData.length === 0) && (!cityData || cityData.length === 0)) {
    return (
      <Card sx={{ 
        boxShadow: { xs: 1, sm: 2 },
        borderRadius: { xs: 1, sm: 2 }
      }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Нет данных о посещениях по странам и городам
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Форматируем данные по странам
  const formattedCountryData = countryData ? countryData.map(item => ({
    code: item.country || 'Неизвестно',
    name: getCountryName(item.country) || 'Неизвестно',
    count: item.count
  })) : [];

  // Форматируем данные по городам
  const formattedCityData = cityData ? cityData
    .filter(item => item.city) // Фильтруем только записи с городами
    .slice(0, 10) // Берем только топ-10 городов
    .map(item => ({
      city: item.city || 'Неизвестно',
      country: getCountryName(item.country) || 'Неизвестно',
      count: item.count
    })) : [];

  return (
    <Grid container spacing={{ xs: 0, sm: 2 }} sx={{ width: '100%', m: 0, p: 0 }}>
      {/* Статистика по странам */}
      {formattedCountryData.length > 0 && (
        <Grid item xs={12} md={6} sx={{ px: 0 }}>
          <Card sx={{ 
            boxShadow: { xs: 1, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
            mb: { xs: 2, sm: 0 }, // Добавляем отступ снизу на мобильных устройствах
            width: '100%'
          }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ 
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                Посещения по странам
              </Typography>
              
              <TableContainer component={Paper} sx={{ 
                boxShadow: 0,
                maxHeight: { xs: 250, sm: 300, md: 350 },
                mt: { xs: 1, sm: 2 },
                overflow: 'auto',
                width: '100%',
                maxWidth: '100%'
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        Страна
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        Посещения
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        %
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formattedCountryData.map((row, index) => {
                      const totalVisits = formattedCountryData.reduce((sum, item) => sum + item.count, 0);
                      const percentage = ((row.count / totalVisits) * 100).toFixed(1);
                      
                      return (
                        <TableRow key={index} hover>
                          <TableCell 
                            component="th" 
                            scope="row"
                            sx={{ 
                              py: { xs: 0.75, sm: 1 },
                              px: { xs: 1, sm: 2 }
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                              {row.name}
                            </Typography>
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ 
                              py: { xs: 0.75, sm: 1 },
                              px: { xs: 1, sm: 2 }
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                              {row.count}
                            </Typography>
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ 
                              py: { xs: 0.75, sm: 1 },
                              px: { xs: 1, sm: 2 }
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                              {percentage}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}
      
      {/* Статистика по городам */}
      {formattedCityData.length > 0 && (
        <Grid item xs={12} md={6} sx={{ px: 0 }}>
          <Card sx={{ 
            boxShadow: { xs: 1, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
            width: '100%'
          }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ 
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                Посещения по городам
              </Typography>
              
              <TableContainer component={Paper} sx={{ 
                boxShadow: 0,
                maxHeight: { xs: 250, sm: 300, md: 350 },
                mt: { xs: 1, sm: 2 },
                overflow: 'auto',
                width: '100%',
                maxWidth: '100%'
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 1, sm: 2 },
                          minWidth: { xs: '80px', sm: 'auto' }
                        }}
                      >
                        Город
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 1, sm: 2 },
                          minWidth: { xs: '80px', sm: 'auto' }
                        }}
                      >
                        Страна
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        Посещения
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formattedCityData.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell 
                          component="th" 
                          scope="row"
                          sx={{ 
                            py: { xs: 0.75, sm: 1 },
                            px: { xs: 1, sm: 2 }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                            {row.city}
                          </Typography>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            py: { xs: 0.75, sm: 1 },
                            px: { xs: 1, sm: 2 }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                            {row.country}
                          </Typography>
                        </TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            py: { xs: 0.75, sm: 1 },
                            px: { xs: 1, sm: 2 }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                            {row.count}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default CountryCityStatistics;
